import { useCallback, useMemo } from 'react';
import { MessagingEtlAmpQueries } from '@frontend/api-messaging-etl-amp';
import { useAppScopeStore } from '@frontend/scope';

export const useOrgData = () => {
  const { selectedOrgId, accessibleLocationData, selectedLocationIds, selectedGroupId } = useAppScopeStore();
  const locationId = selectedLocationIds[0];
  const locationData = accessibleLocationData?.[locationId] ?? {};
  const { data: organizationsData = [] } = MessagingEtlAmpQueries.useGetLocation({ orgId: selectedOrgId });

  const distinctParentIds = useMemo(
    () => [...new Set(organizationsData.map((org) => org.parentId))],
    [organizationsData]
  );

  const locationsWithoutParent = useMemo(
    () => organizationsData.filter((org) => !distinctParentIds.includes(org.locationId)),
    [organizationsData, distinctParentIds]
  );

  const locationIdsWithoutParent = useMemo(
    () =>
      locationsWithoutParent
        .map((org) => org.locationId)
        .filter((location): location is NonNullable<string> => !!location),
    [locationsWithoutParent]
  );

  const getLocationIdsWithoutParent = useCallback(
    (locationIds: string[]) => locationIds.filter((id) => !distinctParentIds.includes(id)),
    [distinctParentIds]
  );

  const getLocationName = useCallback(
    (locationId: string) => organizationsData.find((location) => location.locationId === locationId)?.name ?? '',
    [organizationsData]
  );

  return {
    getLocationIdsWithoutParent,
    getLocationName,
    isParentLocation: selectedGroupId === locationId,
    locationData,
    locationId,
    locationIdsWithoutParent,
    locationsWithoutParent,
    organizationsData,
    orgId: selectedOrgId,
  };
};
